import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { GeminiResponse } from "../types";

export class GeminiService {
  private async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async editImage(base64Image: string | null, prompt: string): Promise<GeminiResponse> {
    const MAX_RETRIES = 3;
    let attempt = 0;

    // Use process.env.API_KEY directly as mapped by vite.config.ts
    const apiKey = process.env.API_KEY;

    if (!apiKey || apiKey === "undefined" || apiKey === "") {
      throw new Error("System Error: API Configuration Missing");
    }

    const ai = new GoogleGenAI({ apiKey });

    while (attempt < MAX_RETRIES) {
      try {
        const parts: any[] = [];
        
        if (base64Image) {
          const base64Data = base64Image.split(',')[1] || base64Image;
          const mimeType = base64Image.match(/data:(.*?);/)?.[1] || 'image/png';
          parts.push({
            inlineData: {
              data: base64Data,
              mimeType: mimeType,
            }
          });
        }

        // BABALVISION ETHICAL CONTENT FILTER PROMPT
        parts.push({
          text: `CORE DIRECTIVE: You are BabalVision AI, a high-fidelity cinematic image engine.
          
          STRICTLY FORBIDDEN (BLOCK ONLY IF 100% MATCH):
          - Meat, cooked meat, raw meat, slaughterhouse visuals.
          - Non-vegetarian food items, including eggs.
          - Explicit gore, excessive blood, or glorified violent cruelty.
          - Sexual content, erotic poses, or personal/romantic physical contact between partners.
          - Revealing or inappropriate clothing.
          - Content that disrespects Hindu culture, traditions, or moral values.

          BY DEFAULT ALLOWED (GENERATE FULLY):
          - People's names (e.g., Balram, Celebrities, Characters).
          - Company names, brands, or product names.
          - Animals, landscapes, objects, tech, fantasy, sci-fi.
          - Animals hunting or killing is also allowed.

          REQUEST: "${prompt}"

          BEHAVIOR:
          1. If safe: Generate/Edit image.
          2. If forbidden: Output ONLY: "Error: Content violates BabalVision AI's policy."
          3. DO NOT output thoughts or JSON.`
        });

        const response: GenerateContentResponse = await ai.models.generateContent({
          model: 'gemini-2.5-flash-image',
          contents: { parts },
          config: {
            imageConfig: {
              aspectRatio: "1:1"
            }
          }
        });

        let imageUrl: string | null = null;
        let textResponse: string | null = null;

        const candidates = response.candidates;
        if (candidates && candidates.length > 0) {
          const candidate = candidates[0];
          if (candidate.content && candidate.content.parts) {
            for (const part of candidate.content.parts) {
              if (part.inlineData) {
                imageUrl = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
              } else if (part.text) {
                const rawText = part.text.trim();
                if (rawText.toLowerCase().includes("violates babalvision policy")) {
                  textResponse = "Error: Content violates BabalVision AI's policy.";
                } else {
                  textResponse = rawText;
                }
              }
            }
          }
        }

        if (!imageUrl && !textResponse) {
          textResponse = "Stay Tuned — We’re Building This for You!";
        }

        return { imageUrl, textResponse };

      } catch (error: any) {
        attempt++;
        console.warn(`Attempt ${attempt} failed:`, error);

        const errorMsg = error?.message || "";

        // Fast fail for safety violations or missing keys (retrying won't fix these)
        if (errorMsg.includes("violates BabalVision policy") || errorMsg.includes("Safety")) {
           throw new Error("Neural Link Blocked: Content safety filters triggered.");
        }
        if (errorMsg.includes("API Configuration Missing")) {
           throw new Error("System Error: API Configuration Missing");
        }

        // If we reached max retries, throw the friendly error
        if (attempt >= MAX_RETRIES) {
           console.error("Max retries reached. Final error:", error);
           throw new Error("Stay Tuned — We’re Building This for You!");
        }

        // Wait before retrying (Exponential backoff: 1s, 2s, 4s...)
        await this.delay(1000 * Math.pow(2, attempt - 1));
      }
    }

    throw new Error("Stay Tuned — We’re Building This for You!");
  }
}

export const geminiService = new GeminiService();